import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../../utils/ErrorModal";

const AdminControl = () => {
  const [approvalLimit, setApprovalLimit] = useState("");
  const [maxApprovalLimit, setMaxApprovalLimit] = useState("");
  const [iaApprovalLimit, setIaApprovalLimit] = useState("");
  const [iaMaxApprovalLimit, setIaMaxApprovalLimit] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));

  useEffect(() => {
    getOrg();
  }, []);

  const getOrg = async () => {
    setFetching(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${user.username}`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      // Existing organizations may have null approvalLimit
      setApprovalLimit(
        res.data?.approvalLimit !== null &&
          res.data?.approvalLimit !== undefined
          ? res.data.approvalLimit
          : "",
      );
      setMaxApprovalLimit(
        res.data?.maxApprovalLimit !== null &&
          res.data?.maxApprovalLimit !== undefined
          ? res.data.maxApprovalLimit
          : "",
      );
      setIaApprovalLimit(
        res.data?.iaApprovalLimit !== null &&
          res.data?.iaApprovalLimit !== undefined
          ? res.data.iaApprovalLimit
          : "",
      );
      setIaMaxApprovalLimit(
        res.data?.iaMaxApprovalLimit !== null &&
          res.data?.iaMaxApprovalLimit !== undefined
          ? res.data.iaMaxApprovalLimit
          : "",
      );
    } catch (error) {
      setOpenError(true);

      setErrorMsg(
        error.response?.data?.Exception ||
          "Unable to fetch organization details",
      );
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    setApprovalLimit(e.target.value);

    setErrors({
      ...errors,
      approvalLimit: "",
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (
      approvalLimit === "" ||
      approvalLimit === null ||
      approvalLimit === undefined
    ) {
      newErrors.approvalLimit = "Approval Limit is required";
    } else if (isNaN(approvalLimit)) {
      newErrors.approvalLimit = "Enter a valid amount";
    } else if (Number(approvalLimit) < 0) {
      newErrors.approvalLimit = "Approval Limit cannot be negative";
    }

    if (
      maxApprovalLimit === "" ||
      maxApprovalLimit === null ||
      maxApprovalLimit === undefined
    ) {
      newErrors.maxApprovalLimit = "Max Approval Limit is required";
    } else if (isNaN(maxApprovalLimit)) {
      newErrors.maxApprovalLimit = "Enter a valid amount";
    } else if (Number(maxApprovalLimit) < 0) {
      newErrors.maxApprovalLimit = "Max Approval Limit cannot be negative";
    } else if (
      approvalLimit !== null &&
      Number(maxApprovalLimit) < Number(approvalLimit)
    ) {
      newErrors.maxApprovalLimit =
        "Max Approval Limit cannot be less than approval limit";
    }

    if (
      iaApprovalLimit === "" ||
      iaApprovalLimit === null ||
      iaApprovalLimit === undefined
    ) {
      newErrors.iaApprovalLimit = "Invoice Approval Limit is required";
    } else if (isNaN(iaApprovalLimit)) {
      newErrors.iaApprovalLimit = "Enter a valid amount";
    } else if (Number(iaApprovalLimit) < 0) {
      newErrors.iaApprovalLimit = "Invoice Approval Limit cannot be negative";
    }

    if (
      iaMaxApprovalLimit === "" ||
      iaMaxApprovalLimit === null ||
      iaMaxApprovalLimit === undefined
    ) {
      newErrors.iaMaxApprovalLimit = "Invoice Max Approval Limit is required";
    } else if (isNaN(iaMaxApprovalLimit)) {
      newErrors.iaMaxApprovalLimit = "Enter a valid amount";
    } else if (Number(iaMaxApprovalLimit) < 0) {
      newErrors.iaMaxApprovalLimit = "Invoice Max Approval Limit cannot be negative";
    } else if (
      iaApprovalLimit !== null &&
      Number(iaMaxApprovalLimit) < Number(iaApprovalLimit)
    ) {
      newErrors.iaMaxApprovalLimit =
        "Invoice Max Approval Limit cannot be less than invoice approval limit";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${orgId}`,
        {
          approvalLimit: Number(approvalLimit),
          maxApprovalLimit: Number(maxApprovalLimit),
          iaApprovalLimit: Number(iaApprovalLimit),
          iaMaxApprovalLimit: Number(iaMaxApprovalLimit),
        },
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      navigate("/admin");
    } catch (error) {
      setOpenError(true);

      setErrorMsg(
        error.response?.data?.Exception ||
          "Something went wrong. Please try again",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/admin");
  };

  return (
    <div style={{ position: "relative" }}>
      {(loading || fetching) && <div className="loading-div"></div>}

      <div className="dashboard-container">
        <Typography variant="h4">Approval Control</Typography>

        <Grid2
          container
          columnSpacing={2}
          sx={{
            width: "60%",
            margin: "auto",
          }}
          py={5}
        >
          <Grid2 size={{ xs: 12 }}>
            <label htmlFor="approvalLimit">Approval Limit Stage I</label>

            <input
              type="number"
              step="0.01"
              min="0"
              id="approvalLimit"
              name="approvalLimit"
              onChange={(e) => {
                setApprovalLimit(e.target.value);
                setErrors({ ...errors, approvalLimit: "" });
              }}
              value={approvalLimit}
              className={`${errors.approvalLimit && "input-error"}`}
              placeholder="Enter approval limit"
            />

            {errors.approvalLimit && (
              <p className="error">{errors.approvalLimit}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <label htmlFor="approvalLimit">Approval Limit Stage II</label>

            <input
              type="number"
              step="0.01"
              min="0"
              id="maxApprovalLimit"
              name="maxApprovalLimit"
              onChange={(e) => {
                setMaxApprovalLimit(e.target.value);
                setErrors({ ...errors, maxApprovalLimit: "" });
              }}
              value={maxApprovalLimit}
              className={`${errors.maxApprovalLimit && "input-error"}`}
              placeholder="Enter approval limit"
            />

            {errors.maxApprovalLimit && (
              <p className="error">{errors.maxApprovalLimit}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <label htmlFor="iaApprovalLimit">Invoice Approval Limit Stage I</label>

            <input
              type="number"
              step="0.01"
              min="0"
              id="iaApprovalLimit"
              name="iaApprovalLimit"
              onChange={(e) => {
                setIaApprovalLimit(e.target.value);
                setErrors({ ...errors, iaApprovalLimit: "" });
              }}
              value={iaApprovalLimit}
              className={`${errors.iaApprovalLimit && "input-error"}`}
              placeholder="Enter approval limit"
            />

            {errors.iaApprovalLimit && (
              <p className="error">{errors.iaApprovalLimit}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <label htmlFor="iaMaxApprovalLimit">Invoice Approval Limit Stage II</label>

            <input
              type="number"
              step="0.01"
              min="0"
              id="iaMaxApprovalLimit"
              name="iaMaxApprovalLimit"
              onChange={(e) => {
                setIaMaxApprovalLimit(e.target.value);
                setErrors({ ...errors, iaMaxApprovalLimit: "" });
              }}
              value={iaMaxApprovalLimit}
              className={`${errors.iaMaxApprovalLimit && "input-error"}`}
              placeholder="Enter approval limit"
            />

            {errors.iaMaxApprovalLimit && (
              <p className="error">{errors.iaMaxApprovalLimit}</p>
            )}
          </Grid2>
        </Grid2>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>

        <Button
          className="btn-contained"
          onClick={handleSubmit}
          disabled={loading || fetching}
        >
          {loading ? "Saving..." : "Save Approval Limit"}
        </Button>
      </Box>

      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default AdminControl;
